import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const isElectron = () => typeof window !== 'undefined' && window.electronAPI !== undefined;


export const LOG_TYPES = {
  CONNECTION: 'connection',
  ACTION: 'action',
  ERROR: 'error',
  SESSION: 'session',
  CONFIG: 'config',
  SECURITY: 'security',
};


export const LOG_CATEGORIES = {
  AUTH: 'auth',
  USER: 'user',
  LOAN: 'loan',
  COVENANT: 'covenant',
  ALERT: 'alert',
  REPORT: 'report',
  IMPORT: 'import',
  SETTINGS: 'settings',
  TEAM: 'team',
};

const ActivityLogContext = createContext(null);

export const ActivityLogProvider = ({ children }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    last24h: 0,
    last7d: 0,
    byType: {
      connection: 0,
      action: 0,
      error: 0,
      session: 0,
      config: 0,
      security: 0,
    },
    errors24h: 0,
  });
  const [isLoading, setIsLoading] = useState(true);


  const loadLogs = useCallback(async (filters = {}) => {
    try {
      if (isElectron()) {
        const result = await window.electronAPI.activityLog.getAll(filters);
        if (result.success) {
          setLogs(result.data);
        }
      } else {
        const { data } = await supabase
          .from('activity_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100);
        setLogs(data || []);
      }
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  }, []);


  const loadStats = useCallback(async () => {
    try {
      if (isElectron()) {
        const result = await window.electronAPI.activityLog.getStats();
        if (result.success) {
          setStats(result.data);
        }
      } else {
        const { data } = await supabase
          .from('activity_logs')
          .select('type, created_at');

        const now = new Date();
        const last24h = new Date(now - 24 * 60 * 60 * 1000);
        const last7d = new Date(now - 7 * 24 * 60 * 60 * 1000);

        setStats({
          total: data?.length || 0,
          last24h: data?.filter(l => new Date(l.created_at) > last24h).length || 0,
          last7d: data?.filter(l => new Date(l.created_at) > last7d).length || 0,
          byType: {
            connection: data?.filter(l => l.type === 'connection').length || 0,
            action: data?.filter(l => l.type === 'action').length || 0,
            error: data?.filter(l => l.type === 'error').length || 0,
            session: data?.filter(l => l.type === 'session').length || 0,
            config: data?.filter(l => l.type === 'config').length || 0,
            security: data?.filter(l => l.type === 'security').length || 0,
          },
          errors24h: data?.filter(l => l.type === 'error' && new Date(l.created_at) > last24h).length || 0,
        });
      }
    } catch (error) {
      console.error('Error loading log stats:', error);
    }
  }, []);


  useEffect(() => {
    const initLoad = async () => {
      setIsLoading(true);
      await Promise.all([loadLogs(), loadStats()]);
      setIsLoading(false);
    };
    initLoad();
  }, [loadLogs, loadStats]);


  const addLog = async (logData) => {
    try {
      if (isElectron()) {
        const result = await window.electronAPI.activityLog.create({
          userId: logData.userId,
          type: logData.type || LOG_TYPES.ACTION,
          category: logData.category || 'general',
          action: logData.action,
          message: logData.message,
          details: logData.details,
          ipAddress: logData.ipAddress || null,
          userAgent: navigator.userAgent,
        });

        if (result.success) {
          setLogs((prevLogs) => [result.data, ...prevLogs].slice(0, 1000));
          loadStats();
          return result.data;
        }
      } else {
        const { data, error } = await supabase
          .from('activity_logs')
          .insert({
            user_id: logData.userId,
            type: logData.type || LOG_TYPES.ACTION,
            category: logData.category || 'general',
            action: logData.action,
            message: logData.message,
            details: logData.details,
            user_agent: navigator.userAgent,
          })
          .select()
          .single();

        if (!error && data) {
          setLogs((prevLogs) => [data, ...prevLogs].slice(0, 1000));
          loadStats();
          return data;
        }
      }
      return null;
    } catch (error) {
      console.error('Error adding log:', error);
      return null;
    }
  };


  const filterLogs = (filters = {}) => {
    const { type, category, userId, startDate, endDate, search } = filters;

    return logs.filter((log) => {
      if (type && log.type !== type) return false;
      if (category && log.category !== category) return false;
      if (userId && log.userId !== userId) return false;
      if (startDate && new Date(log.timestamp) < new Date(startDate)) return false;
      if (endDate && new Date(log.timestamp) > new Date(endDate)) return false;
      if (search) {
        const searchLower = search.toLowerCase();
        return (
          log.message?.toLowerCase().includes(searchLower) ||
          log.userName?.toLowerCase().includes(searchLower) ||
          log.userEmail?.toLowerCase().includes(searchLower) ||
          log.action?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  };


  const getLogStats = () => {
    return stats;
  };


  const fetchLogs = async (filters = {}) => {
    setIsLoading(true);
    await loadLogs(filters);
    await loadStats();
    setIsLoading(false);
  };


  const exportLogs = (format = 'json', filters = {}) => {
    const filteredLogs = filterLogs(filters);

    if (format === 'json') {
      return JSON.stringify(filteredLogs, null, 2);
    }

    if (format === 'csv') {
      const headers = ['Date', 'Type', 'Catégorie', 'Action', 'Message', 'Utilisateur', 'Email', 'IP'];
      const rows = filteredLogs.map((log) => [
        new Date(log.timestamp).toLocaleString('fr-FR'),
        log.type,
        log.category,
        log.action,
        log.message,
        log.userName,
        log.userEmail,
        log.ipAddress || '',
      ]);

      return [headers, ...rows].map((row) => row.join(';')).join('\n');
    }

    return filteredLogs;
  };


  const clearLogs = async (olderThan = null) => {
    try {
      if (isElectron()) {
        if (olderThan) {
          const result = await window.electronAPI.activityLog.delete(olderThan.toISOString());
          if (result.success) {
            await loadLogs();
            await loadStats();
          }
        } else {
          const result = await window.electronAPI.activityLog.deleteAll();
          if (result.success) {
            setLogs([]);
            await loadStats();
          }
        }
      } else {
        if (olderThan) {
          await supabase
            .from('activity_logs')
            .delete()
            .lt('created_at', olderThan.toISOString());
        } else {
          await supabase.from('activity_logs').delete().neq('id', '00000000-0000-0000-0000-000000000000');
        }
        await loadLogs();
        await loadStats();
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
    }
  };

  const value = {
    logs,
    isLoading,
    addLog,
    filterLogs,
    getLogStats,
    exportLogs,
    clearLogs,
    fetchLogs,
    loadLogs,
    loadStats,
    LOG_TYPES,
    LOG_CATEGORIES,
  };

  return <ActivityLogContext.Provider value={value}>{children}</ActivityLogContext.Provider>;
};

export const useActivityLog = () => {
  const context = useContext(ActivityLogContext);
  if (!context) {
    throw new Error('useActivityLog doit être utilisé dans un ActivityLogProvider');
  }
  return context;
};

export default ActivityLogContext;